/**
 * The Arche Store's pages, in the App Store's layout: Today, Apps, Top
 * Charts, Categories and a category, a story, an app's page, search, and the
 * not-found page. Props in, markup out, like every page in this package — the
 * app and the static snapshot render the same markup from the same data.
 *
 * Every page sits in `StoreLayout`: the store's tabs beside (or above) its
 * content.
 */
import type { ReactElement, ReactNode } from 'react';
import { categoryName, useGamesSite } from '../context';
import { Breadcrumbs } from '../components/PublicSiteFrame';
import { paragraphs } from '../format';
import {
  CategoryBanner,
  InfoStrip,
  OpenButton,
  StoreCategoryGrid,
  StoreChart,
  StoreHero,
  StoreIcon,
  StoreNav,
  StoreRow,
  StoreScreenshots,
  StoreSearchForm,
  StoreShelf,
  TodayCard,
  type InfoFact,
} from './components';
import { formatStoreDate, languageName, permissionsInWords, priceLabel, todayLine } from './format';
import {
  STORE_APPS_PATH,
  STORE_CATEGORIES_PATH,
  STORE_GAMES_PATH,
  STORE_HOME_PATH,
  STORE_TOP_PATH,
  advancedSearchPath,
  storeCategoryPath,
  storeSearchPath,
  type StoreTab,
} from './routes';
import type {
  StoreAppPageData,
  StoreAppsPageData,
  StoreCard,
  StoreCategoryRef,
  StoreCollectionData,
  StoreHomeData,
  StoreTopData,
} from './types';

/** The scope every store page renders inside: the Games page's palette, plus the store's own parts. */
export function StoreSurface({ children }: { children: ReactNode }): ReactElement {
  return <div className="fias-games fias-store">{children}</div>;
}

/** The store's tabs beside (wide) or above (narrow) a page. */
export function StoreLayout({
  current,
  gamesShown,
  children,
}: {
  current: StoreTab;
  gamesShown: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="fs-layout">
      <StoreNav current={current} gamesShown={gamesShown} />
      <div className="fs-content">{children}</div>
    </div>
  );
}

/** A page's big title, with what goes beside it (a search field, the date). */
function PageHead({
  title,
  eyebrow,
  aside,
}: {
  title: string;
  eyebrow?: string;
  aside?: ReactNode;
}): ReactElement {
  return (
    <header className="fg-page-head">
      <div>
        {eyebrow && <p className="fs-eyebrow">{eyebrow}</p>}
        <h1 className="fg-large-title">{title}</h1>
      </div>
      {aside}
    </header>
  );
}

function Empty({ title, children }: { title: string; children?: ReactNode }): ReactElement {
  return (
    <div className="fg-empty">
      <p className="fg-empty__title">{title}</p>
      {children}
    </div>
  );
}

/**
 * Today: the date, the editors' cards (stories, collections, the app and game
 * of the day), then the top apps and games. With nothing curated it opens on
 * the featured apps instead, so the tab is never empty.
 */
export function StoreTodayBody({ data }: { data: StoreHomeData }): ReactElement {
  const { now } = useGamesSite();
  return (
    <div className="fg-wrap fs-wrap">
      <PageHead title="Today" eyebrow={todayLine(now) || undefined} />
      {data.today.length > 0 ? (
        <ul className="fs-today-list">
          {data.today.map((card, i) => (
            <li key={`${card.kind}-${card.collection.slug}`}>
              <TodayCard card={card} eager={i === 0} />
            </li>
          ))}
        </ul>
      ) : (
        <StoreHero featured={data.featured} title="Featured today" />
      )}
      <div className="fs-charts">
        <StoreChart
          id="today-top-apps"
          title="Top apps"
          cards={data.topApps}
          limit={6}
          seeAllHref={STORE_TOP_PATH}
          seeAllLabel="See all of the top apps"
        />
        {data.gamesShown && (
          <StoreChart
            id="today-top-games"
            title="Top games"
            cards={data.topGames}
            limit={6}
            seeAllHref={`${STORE_GAMES_PATH}/most-played`}
            seeAllLabel="See all of the most played games"
          />
        )}
      </div>
      {data.today.length === 0 && data.topApps.length === 0 && (
        <Empty title="No apps yet.">
          <p>Apps show up here as soon as their makers publish them.</p>
        </Empty>
      )}
    </div>
  );
}

/**
 * The Apps tab: featured apps, new and updated, the top apps, the editors'
 * collections, every category, and a shelf per category.
 */
export function StoreAppsBody({
  data,
  search,
}: {
  data: StoreHomeData;
  /** The app's live search field, in place of the plain form. */
  search?: ReactNode;
}): ReactElement {
  const categories: StoreCategoryRef[] = data.categories.map((c) => c.category);
  return (
    <div className="fg-wrap fs-wrap">
      <PageHead title="Apps" aside={search ?? <StoreSearchForm />} />
      <StoreHero featured={data.featured} />
      <StoreShelf id="new" title="New and updated" cards={data.newest} />
      <StoreChart
        id="top-apps"
        title="Top apps"
        cards={data.topApps}
        seeAllHref={STORE_TOP_PATH}
        seeAllLabel="See all of the top apps"
      />
      {data.collections.map((collection) => (
        <StoreShelf
          key={collection.slug}
          id={`collection-${collection.slug}`}
          title={collection.title}
          cards={collection.items.map((i) => i.card)}
          seeAllHref={`/a/store/story/${collection.slug}`}
          seeAllLabel={`See all of ${collection.title}`}
          layout="tiles"
        />
      ))}
      <StoreCategoryGrid categories={categories} />
      {data.categories.map(({ category, cards }) => (
        <StoreShelf
          key={category.id}
          id={`category-${category.id}`}
          title={category.label}
          cards={cards}
          seeAllHref={storeCategoryPath(category.id)}
          seeAllLabel={`See all ${category.label} apps`}
          layout="tiles"
        />
      ))}
      {data.newest.length === 0 && data.topApps.length === 0 && (
        <Empty title="No apps yet.">
          <p>Apps show up here as soon as their makers publish them.</p>
        </Empty>
      )}
    </div>
  );
}

/** Every category with apps. */
export function StoreCategoriesBody({
  categories,
}: {
  categories: StoreCategoryRef[];
}): ReactElement {
  const shown = categories.filter((c) => (c.count ?? 0) > 0);
  return (
    <div className="fg-wrap fs-wrap">
      <PageHead title="Categories" />
      {shown.length > 0 ? (
        <StoreCategoryGrid
          categories={shown}
          title="Every category"
          headingId="fs-all-categories"
        />
      ) : (
        <Empty title="No categories yet." />
      )}
    </div>
  );
}

const SORTS: Array<{ id: 'popular' | 'newest' | 'name'; label: string }> = [
  { id: 'popular', label: 'Popular' },
  { id: 'newest', label: 'New' },
  { id: 'name', label: 'A–Z' },
];

/**
 * One category: its banner, its top apps, a sort, and every app in it. A
 * snapshot passes every app; the app passes what it has loaded and
 * `onLoadMore` while there are more.
 */
export function StoreCategoryBody({
  data,
  sort = 'popular',
  onLoadMore,
  loadingMore = false,
}: {
  data: StoreAppsPageData & { category: StoreCategoryRef };
  sort?: 'popular' | 'newest' | 'name';
  onLoadMore?: () => void;
  loadingMore?: boolean;
}): ReactElement {
  const { Link } = useGamesSite();
  const { category, cards, total } = data;
  return (
    <div className="fg-wrap fs-wrap">
      <Breadcrumbs
        trail={[
          { label: 'Apps', href: STORE_APPS_PATH },
          { label: 'Categories', href: STORE_CATEGORIES_PATH },
          { label: category.label },
        ]}
      />
      <CategoryBanner category={category} total={total} />
      <nav aria-label={`Sort ${category.label} apps`} className="fg-sort">
        {SORTS.map((s) => (
          <Link
            key={s.id}
            href={storeCategoryPath(category.id, s.id)}
            className="fg-sort__option"
            aria-current={s.id === sort ? 'page' : undefined}
          >
            {s.label}
          </Link>
        ))}
      </nav>
      {cards.length === 0 ? (
        <Empty title={`No ${category.label} apps yet.`}>
          <p>
            <Link href={STORE_APPS_PATH}>See every app</Link>
          </p>
        </Empty>
      ) : (
        <ul className="fg-list">
          {cards.map((card) => (
            <li key={card.ref}>
              <StoreRow card={card} iconSize="md" headingLevel={2} />
            </li>
          ))}
        </ul>
      )}
      {onLoadMore && (
        <div className="fg-more">
          <button
            type="button"
            className="fg-more__button"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading more apps…' : 'Show more apps'}
          </button>
        </div>
      )}
    </div>
  );
}

/** Top Charts: the most opened apps, in order, with a chip per category. */
export function StoreTopBody({
  data,
  categories = [],
}: {
  data: StoreTopData;
  categories?: StoreCategoryRef[];
}): ReactElement {
  const { Link } = useGamesSite();
  const current = data.category?.id ?? null;
  return (
    <div className="fg-wrap fs-wrap">
      <PageHead title={data.category ? `Top ${data.category.label} apps` : 'Top Charts'} />
      {categories.length > 0 && (
        <nav aria-label="Top apps by category" className="fs-chips">
          <Link
            href={STORE_TOP_PATH}
            className="fs-chip"
            aria-current={current === null ? 'page' : undefined}
          >
            All apps
          </Link>
          {categories
            .filter((c) => (c.count ?? 0) > 0)
            .map((c) => (
              <Link
                key={c.id}
                href={`${STORE_TOP_PATH}?category=${c.id}`}
                className="fs-chip"
                aria-current={current === c.id ? 'page' : undefined}
              >
                {c.label}
              </Link>
            ))}
        </nav>
      )}
      {data.cards.length === 0 ? (
        <Empty title="No apps to rank yet." />
      ) : (
        <ol className="fg-chart fs-chart-page">
          {data.cards.map((card, i) => (
            <li key={card.ref}>
              <StoreRow card={card} rank={i + 1} iconSize="md" headingLevel={2} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** A story or a collection: its art, Fias's words, and the apps it is about. */
export function StoreStoryBody({ story }: { story: StoreCollectionData }): ReactElement {
  const body = paragraphs(story.body ?? '');
  const first = story.items[0]?.card ?? null;
  return (
    <div className="fg-wrap fs-wrap">
      <Breadcrumbs trail={[{ label: 'Today', href: STORE_HOME_PATH }, { label: story.title }]} />
      <article className="fs-story">
        <header className="fs-story__head">
          <div className="fs-story__art">
            {story.heroUrl && story.heroUrlSmall ? (
              <img
                className="fg-hero__picture"
                src={story.heroUrlSmall}
                srcSet={`${story.heroUrlSmall} 800w, ${story.heroUrl} 1600w`}
                sizes="(min-width: 860px) 860px, 100vw"
                alt=""
              />
            ) : (
              first && (
                <div className="fg-hero__art" aria-hidden="true">
                  <StoreIcon card={first} size="xl" />
                </div>
              )
            )}
            <span className="fg-hero__shade" aria-hidden="true" />
            <div className="fs-story__copy">
              {story.kicker && <p className="fg-kicker fg-kicker--on-art">{story.kicker}</p>}
              <h1 className="fs-story__title">{story.title}</h1>
              {story.tagline && <p className="fs-story__tagline">{story.tagline}</p>}
            </div>
          </div>
        </header>
        {body.length > 0 && (
          <div className="fs-story__body">
            {body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        )}
        {story.items.length > 0 && (
          <ul className="fs-story__items">
            {story.items.map(({ card, blurb }) => (
              <li key={card.ref} className="fs-story__item">
                <StoreRow card={card} iconSize="md" headingLevel={2} />
                {blurb && <p className="fs-story__blurb">{blurb}</p>}
              </li>
            ))}
          </ul>
        )}
      </article>
    </div>
  );
}

/**
 * One app's page, in the App Store's order: its header with Open, the facts
 * strip, its description, What's New, what is inside it, what it can use, its
 * in-app purchases, the information table, and what else to open.
 * `actions` goes beside Open — the app's own additions (Add to Worktop,
 * Share); `extra` under it (a Home Screen hint on an iPhone).
 */
export function StoreAppBody({
  data,
  actions,
  extra,
}: {
  data: StoreAppPageData;
  actions?: ReactNode;
  extra?: ReactNode;
}): ReactElement {
  const { Link, signedIn, categoryLabels } = useGamesSite();
  const { card } = data;
  const primary = card.categories[0];
  const about = paragraphs(data.about || card.description);
  const notes = paragraphs(data.releaseNotes ?? '');
  const can = permissionsInWords(data.permissions);
  const price = priceLabel(data.pricing);

  const facts: InfoFact[] = [];
  if (primary) {
    facts.push({
      label: 'Category',
      value: <Link href={storeCategoryPath(primary)}>{categoryName(categoryLabels, primary)}</Link>,
      ...(data.categoryRank
        ? { detail: `#${data.categoryRank.rank} in ${data.categoryRank.label}` }
        : {}),
    });
  }
  if (data.publisher) facts.push({ label: 'Publisher', value: data.publisher.name });
  facts.push({ label: 'Price', value: price });
  facts.push({ label: 'Works in', value: 'Your browser' });
  if (data.version) facts.push({ label: 'Version', value: data.version });

  const information: Array<[string, ReactNode]> = [];
  if (data.publisher) information.push(['Publisher', data.publisher.name]);
  if (card.categories.length > 0) {
    information.push([
      card.categories.length > 1 ? 'Categories' : 'Category',
      card.categories.map((c, i) => (
        <span key={c}>
          {i > 0 && ', '}
          <Link href={storeCategoryPath(c)}>{categoryName(categoryLabels, c)}</Link>
        </span>
      )),
    ]);
  }
  if (card.kind === 'listing') information.push(['Part of', card.archeName]);
  information.push(['Price', price]);
  information.push(['Account', card.requiresSignIn ? 'A free Fias account' : 'None needed']);
  if (data.version) information.push(['Version', data.version]);
  if (data.updatedAt) {
    information.push([
      'Updated',
      <time key="updated" dateTime={data.updatedAt}>
        {formatStoreDate(data.updatedAt)}
      </time>,
    ]);
  }
  if (data.languages.length > 0) {
    information.push(['Languages', data.languages.map(languageName).join(', ')]);
  }
  information.push([
    'Works in',
    'Any modern web browser. Add it to your Home Screen to keep it close.',
  ]);

  return (
    <div className="fg-wrap fs-wrap">
      <Breadcrumbs
        trail={[
          { label: 'Apps', href: STORE_APPS_PATH },
          ...(primary
            ? [{ label: categoryName(categoryLabels, primary), href: storeCategoryPath(primary) }]
            : []),
          { label: card.title },
        ]}
      />
      <header className="fg-detail fs-app">
        <StoreIcon card={card} size="xl" />
        <div className="fg-detail__copy">
          <h1 className="fg-detail__title">{card.title}</h1>
          <p className="fg-detail__sub">
            {card.subtitle ?? (card.kind === 'listing' ? `In ${card.archeName}` : card.description)}
          </p>
          {data.publisher && <p className="fs-app__publisher">{data.publisher.name}</p>}
          <div className="fg-detail__actions">
            <OpenButton card={card} size="large" />
            {actions}
            {card.requiresSignIn && !signedIn && (
              <p className="fg-detail__note">Free to join. You come straight back to the app.</p>
            )}
            {data.inAppPurchases.length > 0 && <p className="fs-app__iap-note">In-app purchases</p>}
          </div>
          {extra}
        </div>
      </header>

      <InfoStrip facts={facts} />

      <StoreScreenshots shots={data.screenshots} appTitle={card.title} />

      <section className="fg-about" aria-labelledby="fs-about-title">
        <h2 id="fs-about-title" className="fg-h2">
          About {card.title}
        </h2>
        {about.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </section>

      {notes.length > 0 && (
        <section className="fg-about fs-whats-new" aria-labelledby="fs-new-title">
          <div className="fs-section-head">
            <h2 id="fs-new-title" className="fg-h2">
              What’s New
            </h2>
            <p className="fs-section-head__meta">
              {data.version && <span>Version {data.version}</span>}
              {data.updatedAt && (
                <time dateTime={data.updatedAt}>{formatStoreDate(data.updatedAt)}</time>
              )}
            </p>
          </div>
          {notes.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </section>
      )}

      <StoreShelf
        id="inside"
        title={`Inside ${card.title}`}
        cards={data.insideThisApp}
        layout="tiles"
      />

      {can.length > 0 && (
        <section className="fg-about fs-can" aria-labelledby="fs-can-title">
          <h2 id="fs-can-title" className="fg-h2">
            What this app can use
          </h2>
          <ul className="fs-can__list">
            {can.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p className="fs-can__note">
            Fias asks for your approval before an app reaches your files, and you can change it any
            time.
          </p>
        </section>
      )}

      {data.inAppPurchases.length > 0 && (
        <section className="fg-about" aria-labelledby="fs-iap-title">
          <h2 id="fs-iap-title" className="fg-h2">
            In-app purchases
          </h2>
          <dl className="fs-table">
            {data.inAppPurchases.map((p) => (
              <div key={p.name} className="fs-table__row">
                <dt>{p.name}</dt>
                <dd>{p.priceCredits.toLocaleString('en-US')} credits</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      <section className="fg-about" aria-labelledby="fs-info-title">
        <h2 id="fs-info-title" className="fg-h2">
          Information
        </h2>
        <dl className="fs-table">
          {information.map(([label, value]) => (
            <div key={label} className="fs-table__row">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <StoreShelf
        id="publisher"
        title={data.publisher ? `More from ${data.publisher.name}` : 'More from this publisher'}
        cards={data.moreFromPublisher}
        layout="tiles"
      />
      <StoreShelf id="related" title="You might also like" cards={data.related} layout="tiles" />
    </div>
  );
}

/**
 * Search: the field, then the results (games and apps together) — or, before
 * a search, the editors' suggestions and every category. Always offers the
 * Advanced search, carrying the words.
 */
export function StoreSearchBody({
  query,
  cards,
  suggestions = [],
  categories = [],
  search,
  status,
}: {
  query: string;
  cards: StoreCard[] | null;
  suggestions?: string[];
  categories?: StoreCategoryRef[];
  /** The app's live search field, in place of the plain form. */
  search?: ReactNode;
  /** What a screen reader hears as results change. */
  status?: string;
}): ReactElement {
  const { Link } = useGamesSite();
  return (
    <div className="fg-wrap fs-wrap">
      <PageHead title="Search" aside={search ?? <StoreSearchForm defaultValue={query} />} />
      <p className="fg-visually-hidden" role="status" aria-live="polite">
        {status ?? ''}
      </p>
      {!query && (
        <>
          {suggestions.length > 0 && (
            <section className="fg-shelf" aria-labelledby="fs-discover">
              <div className="fg-shelf__head">
                <h2 id="fs-discover" className="fg-h2">
                  Discover
                </h2>
              </div>
              <ul className="fs-discover">
                {suggestions.map((phrase) => (
                  <li key={phrase}>
                    <Link href={storeSearchPath(phrase)} className="fs-discover__link">
                      {phrase}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <StoreCategoryGrid categories={categories} title="Browse by category" />
        </>
      )}
      {query && cards && cards.length === 0 && (
        <Empty title={`Nothing on the Arche Store for “${query}”.`}>
          <p>Try other words, or the Advanced search.</p>
        </Empty>
      )}
      {query && cards && cards.length > 0 && (
        <ul className="fg-list">
          {cards.map((card) => (
            <li key={card.ref}>
              <StoreRow card={card} iconSize="md" headingLevel={2} />
            </li>
          ))}
        </ul>
      )}
      <p className="fs-advanced">
        Looking for something specific?{' '}
        <Link href={advancedSearchPath(query || undefined)}>Advanced search</Link> filters by type
        and sorts by quality.
      </p>
    </div>
  );
}

/** An address that is not (or no longer) on the store. */
export function StoreNotFoundBody(): ReactElement {
  const { Link } = useGamesSite();
  return (
    <div className="fg-wrap fs-wrap">
      <div className="fg-empty fg-empty--page">
        <h1 className="fg-empty__title">This isn’t on the Arche Store.</h1>
        <p>It may have moved, or its maker took it down.</p>
        <p>
          <Link href={STORE_APPS_PATH} className="fg-play fg-play--large">
            Browse apps
          </Link>
        </p>
      </div>
    </div>
  );
}
